import { AskAlpharunPanel } from "@repo/ui/chat/askAlpharunPanel";
import { type FC, useState } from "react";
import { ResearchAgentChatView } from "./copilotChatView.js";
import type { ResearchAgentProps } from "./copilotContext.js";
import { ResearchAgentHistoryView } from "./copilotHistoryView.js";

/** Content column inside the panel (header, messages, composer). */
const PANEL_CONTENT_WIDTH_PX = 310;

/**
 * Gutter between the body card and the panel content. (The app layout's own
 * padding already provides the gutter at the window edge.)
 */
const PANEL_HORIZONTAL_MARGIN_PX = 8;

/** Default (and minimum) width of the docked panel column. */
export const RESEARCH_AGENT_PANEL_WIDTH_PX =
  PANEL_CONTENT_WIDTH_PX + PANEL_HORIZONTAL_MARGIN_PX;

/** How far the panel column can be resized. */
export const RESEARCH_AGENT_PANEL_MAX_WIDTH_PX = 640;

export interface ResearchAgentSidePanelProps {
  researchAgent: ResearchAgentProps;
}

/**
 * Docked research agent conversation and history with a persistent toolbar.
 */
export const ResearchAgentSidePanel: FC<ResearchAgentSidePanelProps> = ({
  researchAgent,
}) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [didShowHistory, setDidShowHistory] = useState(false);
  const handleHistoryOpenChange = (isOpen: boolean) => {
    if (isOpen) setDidShowHistory(true);
    setIsHistoryOpen(isOpen);
  };

  return (
    <AskAlpharunPanel
      // The app layout's `p-2` already covers the top/bottom/right gutters;
      // keep only the left gutter between the body card and the panel.
      className="h-full w-full py-0 pr-0 pl-2"
    >
      {/* Only one view of the main chat renders: the modal takes it over. */}
      {!researchAgent.isModalOpen ? (
        <ResearchAgentChatView
          copilotChat={researchAgent}
          surface="panel"
          onRequestClose={researchAgent.onCloseChat}
          onRequestExpand={() => researchAgent.onModalOpenChange(true)}
          isHistoryOpen={isHistoryOpen}
          onHistoryOpenChange={handleHistoryOpenChange}
          history={
            didShowHistory ? (
              <ResearchAgentHistoryView
                surface="panel"
                resourceId={researchAgent.resourceId}
                selectedThreadId={researchAgent.threadId}
                onThreadSelect={(thread) => {
                  researchAgent.onThreadSelect(thread);
                  setIsHistoryOpen(false);
                }}
                onNewThreadSelect={() => {
                  researchAgent.onNewThreadSelect();
                  setIsHistoryOpen(false);
                }}
              />
            ) : undefined
          }
        />
      ) : null}
    </AskAlpharunPanel>
  );
};
